import { FlatTreeControl } from "@angular/cdk/tree";
import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { MatTreeFlatDataSource, MatTreeFlattener, MatTreeNode } from "@angular/material/tree";
import { ClassificationService } from "../work-classification.service";
import { WorkClassification } from "src/app/shared/Models/hourentry";
import { AppState } from "src/app/state/app.reducer";
import { Store } from "@ngrx/store";
import { selectCurrentProject, selectCurrentProjectId } from "src/app/state/app.selectors";
import { WorkClassificationFormComponent } from "../work-classification-form/work-classification-form.component";
import { MatDialog } from "@angular/material/dialog";
import { BaseComponent } from "src/app/shared/components/base-component";
import { ConfirmationService } from "src/app/shared/services/utilities/confirmation.service";
import { CopyClassificationsToProjectDialogComponent } from "./copy-to-project-dialog/copy-to-project-dialog.component";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { firstValueFrom } from 'rxjs';
import { SnackBarService } from 'src/app/shared/services/snack-service.service';
import { TranslateService } from '@ngx-translate/core';
import { LoggerService } from 'src/app/shared/services/logger.service';
import { ApplyTemplateDialogComponent, ApplyTemplateDialogData } from '../templates/apply-template-dialog/apply-template-dialog.component';
import { CopyFromProjectDialogComponent, CopyFromProjectDialogData } from '../templates/copy-from-project-dialog/copy-from-project-dialog.component';
import { mapTemplateError } from '../templates/template-error';
import { ApplyTemplateResult, WorkClassificationTemplate } from '../templates/work-classification-template.models';
import { SyncTemplateDialogComponent, SyncTemplateDialogData } from '../templates/sync-template-dialog/sync-template-dialog.component';
import { BILLED_RENAMES_WARNING, SyncResult } from '../templates/work-classification-template.models';
import { LinkTemplateDialogComponent, LinkTemplateDialogData, LinkTemplateDialogResult } from '../templates/link-template-dialog/link-template-dialog.component';
import { WorkClassificationTemplateService } from '../templates/work-classification-template.service';


interface ClassificationNode {
  name: string;
  description: string;
  children: ClassificationNode[];
  parentClassificationId: number;
  id: number;
  level: number;
  expandable: boolean;
}

@Component({
    selector: 'app-classification-tree',
    templateUrl: './work-classification-tree.component.html',
    styleUrls: ['./work-classification-tree.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WorkClassificationTreeComponent extends BaseComponent implements OnInit {
  project = this.store.select(selectCurrentProject);
  expandedNodeIds: number[] = [];
  nodeStates: { [key: number]: string } = {};
  treeControl = new FlatTreeControl<ClassificationNode>(
    node => node.level,
    node => node.expandable,
  );
  classifications: WorkClassification[] = [];
  flatClassifications: WorkClassification[] = [];
  private _transformer = (node: ClassificationNode, level: number) => {
    return {
      expandable: !!node.children && node.children.length > 0,
      name: node.name,
      level: level,
      id: node.id,
      children: node.children,
      description: node.description,
      parentClassificationId: node.parentClassificationId,
    };
  };
  treeFlattener = new MatTreeFlattener(
    this._transformer,
    node => node.level,
    node => node.expandable,
    node => node.children,
  );
  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  projectId: number | undefined;
  newNodeId: number | null = null;
  isExporting: boolean = false;
  /** True until the first classification load for the current project has finished. */
  loading = true;
  /** Active templates, loaded only when the tree is empty (for the "Käytä mallia" tile). */
  templates: WorkClassificationTemplate[] = [];
  /** True when the template list call failed (so "no templates" is not claimed). */
  templatesFailed = false;
  /** Template the tree is linked to (any node carries a templateId), or null for an unlinked tree. */
  linkedTemplateId: number | null = null;
  linkedTemplateName: string | null = null;

  constructor(
    private classificationService: ClassificationService,
    private store: Store<AppState>,
    private dialog: MatDialog,
    private confirmService: ConfirmationService,
    private snackBarService: SnackBarService,
    private translate: TranslateService
  ) {
    super();
  }

  private logger = inject(LoggerService);
  private templateService = inject(WorkClassificationTemplateService);

  ngOnInit(): void {
    this.store.select(selectCurrentProjectId).subscribe(projectId => {
      if (projectId) {
        this.projectId = projectId;
        this.loadClassifications(projectId);
      }
    });
  }

  setNodeHighlight(nodeId: number): void {
    this.nodeStates[nodeId] = 'highlighted';
    setTimeout(() => {
      this.nodeStates[nodeId] = 'normal';
    }, 1000);
  }

  loadClassifications(projectId: number) {
    this.loading = true;
    this.classificationService.getClassifications(projectId).subscribe({
      next: classifications => {
        this.dataSource.data = classifications.map(c => this.convertToClassificationNode(c, 0));
        this.classifications = classifications;
        this.flatClassifications = classifications.flatMap(c => this.flattenClassifications(c));
        this.restoreExpandedNodes();
        this.loading = false;
        this.linkedTemplateId = this.flatClassifications.find(c => c.templateId != null)?.templateId ?? null;
        if (classifications.length === 0) {
          this.loadTemplates();
        } else if (this.linkedTemplateId !== null) {
          this.loadLinkedTemplateName(this.linkedTemplateId);
        }
      },
      error: error => {
        this.loading = false;
        this.logger.errorWithContext('WorkClassificationTreeComponent', 'Failed to load classifications', error);
      },
    });
  }

  loadTemplates(): void {
    this.templatesFailed = false;
    this.templateService.list().subscribe({
      next: templates => (this.templates = templates),
      error: error => {
        this.templates = [];
        this.templatesFailed = true;
        this.logger.errorWithContext('WorkClassificationTreeComponent', 'Failed to load templates', error);
      },
    });
  }

  /** Resolves the provenance chip's template name (inactive templates included). */
  private loadLinkedTemplateName(templateId: number): void {
    if (this.linkedTemplateName !== null && this.templates.some(t => t.id === templateId && t.name === this.linkedTemplateName)) {
      return;
    }
    this.templateService.list(true).subscribe({
      next: list => (this.linkedTemplateName = list.find(t => t.id === templateId)?.name ?? null),
      error: () => (this.linkedTemplateName = null),
    });
  }

  openLinkTemplate(): void {
    const data: LinkTemplateDialogData = { projectId: this.projectId! };
    this.dialog
      .open<LinkTemplateDialogComponent, LinkTemplateDialogData, LinkTemplateDialogResult>(LinkTemplateDialogComponent, {
        width: '44rem',
        maxWidth: '95vw',
        autoFocus: false,
        panelClass: ['mobile-full-dialog'],
        data,
      })
      .afterClosed()
      .subscribe(result => this.handleLinkResult(result));
  }

  handleLinkResult(result: LinkTemplateDialogResult | undefined): void {
    if (!result) {
      return;
    }
    this.linkedTemplateName = result.templateName;
    const ref = this.snackBarService.success(
      this.translate.instant('WORKCLASSIFICATION.TEMPLATES.LINK.LINKED', { name: result.templateName, count: result.linked }),
      { duration: 8000, action: this.translate.instant('WORKCLASSIFICATION.TEMPLATES.LINK.UPDATE_NOW') }
    );
    // "Päivitä nyt" goes straight to the sync preview.
    ref?.onAction().subscribe(() => this.openSyncFromTemplate(result.templateId, result.templateName));
    this.loadClassifications(this.projectId!);
  }

  async openSyncFromTemplate(templateId: number | null = this.linkedTemplateId, templateName: string | null = this.linkedTemplateName): Promise<void> {
    if (templateId == null || this.projectId == null) {
      return;
    }
    const project = await firstValueFrom(this.project);
    const data: SyncTemplateDialogData = { projectId: this.projectId, projectName: project?.name ?? null, templateId, templateName };
    this.dialog
      .open<SyncTemplateDialogComponent, SyncTemplateDialogData, SyncResult>(SyncTemplateDialogComponent, {
        width: '48rem',
        maxWidth: '95vw',
        autoFocus: false,
        panelClass: ['mobile-full-dialog'],
        data,
      })
      .afterClosed()
      .subscribe(result => this.handleSyncResult(result));
  }

  /** The counts always come from the response; the template may have changed since the preview. */
  handleSyncResult(result: SyncResult | undefined): void {
    if (!result) {
      return;
    }
    const ref = this.snackBarService.success(
      this.translate.instant('WORKCLASSIFICATION.TEMPLATES.SYNC.MSG_DONE', { added: result.added, renamed: result.renamed }),
      { duration: 5000 }
    );
    if (result.warning === BILLED_RENAMES_WARNING) {
      const warn = () => this.snackBarService.warning(this.translate.instant('WORKCLASSIFICATION.TEMPLATES.SYNC.MSG_BILLED_WARNING'), { duration: 12000 });
      if (ref) {
        ref.afterDismissed().subscribe(warn);
      } else {
        warn();
      }
    }
    this.loadClassifications(this.projectId!);
  }

  openApplyTemplate(): void {
    const data: ApplyTemplateDialogData = { projectId: this.projectId! };
    this.dialog
      .open<ApplyTemplateDialogComponent, ApplyTemplateDialogData, ApplyTemplateResult>(ApplyTemplateDialogComponent, {
        width: '40rem',
        maxWidth: '95vw',
        autoFocus: false,
        panelClass: ['mobile-full-dialog'],
        data,
      })
      .afterClosed()
      .subscribe(result => this.handleApplyResult(result));
  }

  /** onlyIfEmpty can come back as "not applied" when someone added nodes meanwhile. */
  handleApplyResult(result: ApplyTemplateResult | undefined): void {
    if (!result) {
      return;
    }
    if (result.applied) {
      this.snackBarService.success(this.translate.instant('WORKCLASSIFICATION.TEMPLATES.TREE.APPLIED', { count: result.nodesCreated }));
    } else {
      this.snackBarService.info(this.translate.instant('WORKCLASSIFICATION.TEMPLATES.TREE.ALREADY_HAS_TREE'));
    }
    this.loadClassifications(this.projectId!);
  }

  openCopyFromProject(): void {
    const data: CopyFromProjectDialogData = { targetProjectId: this.projectId! };
    this.dialog
      .open<CopyFromProjectDialogComponent, CopyFromProjectDialogData, boolean>(CopyFromProjectDialogComponent, {
        width: '40rem',
        maxWidth: '95vw',
        autoFocus: false,
        panelClass: ['mobile-full-dialog'],
        data,
      })
      .afterClosed()
      .subscribe(copied => {
        if (copied) {
          this.snackBarService.success(this.translate.instant('WORKCLASSIFICATION.TEMPLATES.TREE.COPIED'));
          this.loadClassifications(this.projectId!);
        }
      });
  }
flattenClassifications(classification: WorkClassification): WorkClassification[] {
    return [classification, ...(classification.children || []).flatMap(c => this.flattenClassifications(c))];
  }
  convertToClassificationNode(classification: WorkClassification, level: number): ClassificationNode {
    return {
      name: classification.name,
      description: classification.description,
      id: classification.id,
      level: level,
      children: classification.children?.map(sub => this.convertToClassificationNode(sub, level + 1)) || [],
      parentClassificationId: classification.parentId || 0,
      expandable: !!classification.children && classification.children.length > 0,
    };
  }

  copyAllClassifications() {
    let dialogRef = this.dialog.open(CopyClassificationsToProjectDialogComponent, {
      width: '35rem',
      autoFocus: false,
      data: { fromProject: this.projectId },
    });

    dialogRef.afterClosed().subscribe((projectId: number) => {
      if (projectId) {
        this.classificationService.copyClassifications(this.projectId!, projectId).subscribe({
          next: () => this.loadClassifications(this.projectId!),
          // 409 TARGET_TREE_IN_USE and 400 SOURCE_TREE_EMPTY used to fail silently.
          error: error => {
            const m = mapTemplateError(error);
            this.snackBarService.error(this.translate.instant(m.key, m.params));
          },
        });
      }
    });
  }

  addSubClassification(node: ClassificationNode | null): void {
    const dialogRef = this.dialog.open(WorkClassificationFormComponent, {
      width: '35rem',
      autoFocus: false,
      data: { parentId: node ? node.id : null },
    });
    dialogRef.componentInstance.classificationCreated.subscribe((newClassification: WorkClassification) => {
      this.saveExpandedNodes();
      if (newClassification.parentId && !this.expandedNodeIds.includes(newClassification.parentId)) {
        this.expandedNodeIds.push(newClassification.parentId);
      }
      this.newNodeId = newClassification.id;
      this.setNodeHighlight(this.newNodeId);
      this.loadClassifications(newClassification.projectId);
      this.restoreExpandedNodes();
    });
  }

  saveExpandedNodes(): void {
    this.expandedNodeIds = this.treeControl.dataNodes
      .filter(node => this.treeControl.isExpanded(node))
      .map(node => node.id);
  }

  restoreExpandedNodes(): void {
    this.treeControl.dataNodes.forEach(node => {
      if (this.expandedNodeIds.includes(node.id)) {
        this.treeControl.expand(node);
      }
    });
  }

  editClassification(node: ClassificationNode): void {
    const classification = this.flatClassifications.find(c => c.id === node.id);
    const dialogRef = this.dialog.open(WorkClassificationFormComponent, {
      width: '35rem',
      autoFocus: false,
      disableClose: true,
      data: { classification: classification },
    });

    dialogRef.componentInstance.classificationCreated.subscribe((updatedClassification: WorkClassification) => {
      this.saveExpandedNodes();
      if (updatedClassification.parentId && !this.expandedNodeIds.includes(updatedClassification.parentId)) {
        this.expandedNodeIds.push(updatedClassification.parentId);
      }
      this.newNodeId = updatedClassification.id;
      this.setNodeHighlight(this.newNodeId);
      this.loadClassifications(updatedClassification.projectId);
    });
  }

  deleteClassification(node: ClassificationNode): void {
    this.confirmService.confirm('Are you sure you want to permanently delete this classification?', 'Confirm', () => {
      this.classificationService.deleteClassification(node.id).subscribe(
        () => {
          this.saveExpandedNodes();
          this.loadClassifications(this.projectId!);
        },
        error => {
          // Error handled silently, snackbar will be shown by service
        }
      );
    }, () => { }, 'Delete', 'warn', 'delete');
  }

  async exportTreeToPDF(): Promise<void> {
    this.isExporting = true;

    try {
      // Get current project
      const currentProject = await firstValueFrom(this.project);
      if (!currentProject) {
        throw new Error('No project selected');
      }

      // Create PDF
      const pdf = new jsPDF('portrait', 'px', 'a4', true);

      // Set PDF metadata
      pdf.setProperties({
        title: this.translate.instant('WORKCLASSIFICATION.EXPORTTITLE'),
        subject: 'LTQ-Rakennus Oy',
        author: 'LTQ-Rakennus Oy',
      });

      // Try to load logo (optional - continue without if fails)
      let startY = 40;
      try {
        const logoUrl = 'assets/images/ltqrakennus.png';
        const logoImage = await fetch(logoUrl)
          .then((r) => r.blob())
          .then((b) => URL.createObjectURL(b));
        pdf.addImage(logoImage, 'PNG', 10, 10, 80, 48, '', 'SLOW');
        startY = 80;
      } catch (logoError) {
        // Logo load failed - continue without it
      }

      // Add title
      pdf.setFontSize(22);
      const title = `${this.translate.instant('WORKCLASSIFICATION.EXPORTTITLE')} - ${currentProject.name}`;
      const pageWidth = pdf.internal.pageSize.width;
      const pageHeight = pdf.internal.pageSize.height;
      const txtWidth = (pdf.getStringUnitWidth(title) * pdf.getFontSize()) / pdf.internal.scaleFactor;
      const x = (pageWidth - txtWidth) / 2;
      pdf.text(title, x, 30);

      let currentY = startY;

      // Generate separate table for each root classification
      for (const rootClassification of this.classifications) {
        // Check if we need a new page
        if (currentY > pageHeight - 100) {
          pdf.addPage();
          currentY = 40;
        }

        // Add root classification title
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text(rootClassification.name, 15, currentY);
        currentY += 20;

        // Add root description if exists
        if (rootClassification.description) {
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'italic');
          const descLines = pdf.splitTextToSize(rootClassification.description, pageWidth - 30);
          pdf.text(descLines, 15, currentY);
          currentY += (descLines.length * 12) + 10;
        }

        // Build table data for children only
        const tableData = this.buildPDFTableDataForChildren(rootClassification.children || []);

        if (tableData.length > 0) {
          // Generate table for children
          autoTable(pdf, {
            startY: currentY,
            head: [['Alaluokitus', 'Kuvaus']],
            body: tableData,
            theme: 'grid',
            columnStyles: {
              0: { cellWidth: 'auto' },
              1: { cellWidth: 'auto' }
            },
            margin: { left: 15, right: 15 }
          });

          currentY = (pdf as any).lastAutoTable.finalY + 20;
        } else {
          currentY += 10;
        }
      }

      // Save PDF
      const fileName = `Tuntiluokittelut_${currentProject.name}.pdf`;
      pdf.save(fileName);

    } catch (error) {
      this.logger.errorWithContext('WorkClassificationTreeComponent', 'Failed to export PDF', error as Error);
      this.snackBarService.error(
        this.translate.instant('WORKCLASSIFICATION.EXPORTFAILED')
      );
    } finally {
      this.isExporting = false;
    }
  }

  private buildPDFTableData(classifications: WorkClassification[], level: number = 0): string[][] {
    const rows: string[][] = [];

    for (const classification of classifications) {
      // Create indented name using spaces and hyphen prefix
      const indent = '  '.repeat(level);
      const prefix = level > 0 ? '- ' : '';
      const indentedName = indent + prefix + classification.name;
      const description = classification.description || '';

      rows.push([indentedName, description]);

      // Recursively add children
      if (classification.children && classification.children.length > 0) {
        rows.push(...this.buildPDFTableData(classification.children, level + 1));
      }
    }

    return rows;
  }

  private buildPDFTableDataForChildren(classifications: WorkClassification[], level: number = 0): string[][] {
    const rows: string[][] = [];

    for (const classification of classifications) {
      // Create indented name using spaces only
      const indent = '  '.repeat(level);
      const indentedName = indent + classification.name;
      const description = classification.description || '';

      rows.push([indentedName, description]);

      // Recursively add children
      if (classification.children && classification.children.length > 0) {
        rows.push(...this.buildPDFTableDataForChildren(classification.children, level + 1));
      }
    }

    return rows;
  }

  hasChild = (_: number, node: ClassificationNode) => node.expandable;
}
